import { type ReactNode } from 'react';
import { ArrowDown, ArrowUp, Minus } from 'lucide-react';
import { tv, type VariantProps } from 'tailwind-variants';

const kpiCard = tv({
  base: [
    /* No overflow-hidden: MetricExplainButton uses in-tree Popover (no Portal); clipping made the “i” panel flicker open then vanish. */
    'flex h-full w-full min-w-0 flex-col items-center rounded-xl border border-[var(--color-rail-card-border)] bg-[var(--color-an-surface)] text-center',
    'shadow-sm',
  ].join(' '),
  variants: {
    tone: {
      default: '',
      success: 'border-[color-mix(in_srgb,var(--color-an-success)_35%,var(--color-an-border))]',
      warn: 'border-[color-mix(in_srgb,var(--color-an-warn)_35%,var(--color-an-border))]',
      danger: 'border-[color-mix(in_srgb,var(--color-an-danger)_35%,var(--color-an-border))]',
    },
    /**
     * Fix 7 / AC-06: dual-strip ~88–104px readable height @320.
     * Dense only below md — desktop chrome stays default p-4 / icon / value scale.
     */
    compact: {
      false: 'p-4',
      true: 'p-3 md:p-4',
    },
  },
  defaultVariants: { tone: 'default', compact: false },
});

const iconWell = tv({
  base: ['flex shrink-0 items-center justify-center rounded-lg'].join(' '),
  variants: {
    tone: {
      default: 'bg-[var(--color-an-bg-elevated)] text-[var(--color-an-text-muted)]',
      success: 'bg-[var(--color-success-light)] text-[var(--color-rail-kpi-up)]',
      warn: 'bg-[var(--color-warning-light)] text-[var(--color-an-warn)]',
      danger: 'bg-[var(--color-error-light)] text-[var(--color-an-danger)]',
      chart1: 'bg-[color-mix(in_srgb,var(--color-an-chart-1)_22%,var(--color-an-surface))] text-[var(--color-an-chart-1)]',
      chart2: 'bg-[color-mix(in_srgb,var(--color-an-chart-2)_22%,var(--color-an-surface))] text-[var(--color-an-chart-2)]',
      chart3: 'bg-[color-mix(in_srgb,var(--color-an-chart-3)_22%,var(--color-an-surface))] text-[var(--color-an-chart-3)]',
      chart5: 'bg-[color-mix(in_srgb,var(--color-an-chart-5)_22%,var(--color-an-surface))] text-[var(--color-an-chart-5)]',
      chart6: 'bg-[color-mix(in_srgb,var(--color-an-chart-6,var(--color-an-danger))_22%,var(--color-an-surface))] text-[var(--color-an-chart-6,var(--color-an-danger))]',
      chart7: 'bg-[color-mix(in_srgb,var(--color-an-chart-7)_22%,var(--color-an-surface))] text-[var(--color-an-chart-7)]',
    },
    compact: {
      false: 'h-8 w-8 [&>svg]:h-4 [&>svg]:w-4',
      true: 'h-7 w-7 [&>svg]:h-3.5 [&>svg]:w-3.5 md:h-8 md:w-8 md:[&>svg]:h-4 md:[&>svg]:w-4',
    },
  },
  defaultVariants: { tone: 'default', compact: false },
});

const headerStack = tv({
  base: 'flex w-full min-w-0 flex-col items-center',
  variants: {
    compact: {
      false: 'gap-2',
      true: 'gap-1.5 md:gap-2',
    },
  },
  defaultVariants: { compact: false },
});

/** Full label visible — wrap at word boundaries only (never mid-word / never ellipsis). */
const label = tv({
  base: 'min-w-0 max-w-full whitespace-normal break-normal text-xs font-medium uppercase tracking-wide text-[var(--color-an-text-muted)] leading-snug text-center',
});
const value = tv({
  base: 'min-w-0 w-full text-center font-bold tracking-tight text-[var(--color-an-text)]',
  variants: {
    overflow: {
      truncate: 'truncate tabular-nums leading-none',
      wrap: 'whitespace-normal break-words leading-snug [overflow-wrap:anywhere]',
    },
    compact: {
      false: 'mt-2',
      true: 'mt-1.5 md:mt-2',
    },
  },
  compoundVariants: [
    { overflow: 'truncate', compact: false, class: 'text-2xl' },
    { overflow: 'truncate', compact: true, class: 'text-xl md:text-2xl' },
    { overflow: 'wrap', compact: false, class: 'text-lg' },
    { overflow: 'wrap', compact: true, class: 'text-base md:text-lg' },
  ],
  defaultVariants: { overflow: 'truncate', compact: false },
});
const delta = tv({
  base: [
    'inline-flex max-w-full items-center justify-center gap-1 self-center rounded-full px-2 py-0.5',
    'min-w-0 text-xs font-medium leading-snug',
    '[&>svg]:h-3.5 [&>svg]:w-3.5 [&>svg]:shrink-0',
  ].join(' '),
  variants: {
    direction: {
      up: 'bg-[var(--color-success-light)] text-[var(--color-rail-kpi-up)]',
      down: 'bg-[var(--color-error-light)] text-[var(--color-rail-kpi-down)]',
      flat: 'bg-[var(--color-an-bg-elevated)] text-[var(--color-an-text-muted)]',
    },
    compact: {
      false: 'mt-1.5',
      true: 'mt-1 md:mt-1.5',
    },
  },
  defaultVariants: { direction: 'flat', compact: false },
});
type KpiCardTone = NonNullable<VariantProps<typeof kpiCard>['tone']>;
type IconWellTone = NonNullable<VariantProps<typeof iconWell>['tone']>;
type DeltaDirection = NonNullable<VariantProps<typeof delta>['direction']>;

/** Spec1 §8.6 Czech semantic labels → existing tone tokens. */
const SEMANTIC_TONE_ALIASES = {
  úspěch: 'success',
  upozornění: 'warn',
  chyba: 'danger',
  neutrální: 'default',
} as const;

type SemanticToneAlias = keyof typeof SEMANTIC_TONE_ALIASES;
type KpiCardToneInput = KpiCardTone | SemanticToneAlias;

function resolveKpiCardTone(tone: KpiCardToneInput | undefined): KpiCardTone {
  if (tone == null) {
    return 'default';
  }
  if (tone in SEMANTIC_TONE_ALIASES) {
    return SEMANTIC_TONE_ALIASES[tone as SemanticToneAlias];
  }
  return tone as KpiCardTone;
}

function TrendDirectionIcon({ direction }: { readonly direction: DeltaDirection }): JSX.Element {
  if (direction === 'up') {
    return <ArrowUp aria-hidden data-testid="kpi-card-trend-icon-up" />;
  }
  if (direction === 'down') {
    return <ArrowDown aria-hidden data-testid="kpi-card-trend-icon-down" />;
  }
  return <Minus aria-hidden data-testid="kpi-card-trend-icon-flat" />;
}

export interface KpiCardProps {
  readonly label: string;
  readonly value: string;
  readonly deltaLabel?: string;
  readonly direction?: DeltaDirection;
  /**
   * Card border tone only — never chart wells.
   * Spec1 §8.6: úspěch→success, upozornění→warn, chyba→danger, neutrální→default.
   */
  readonly tone?: KpiCardToneInput;
  /** Icon well tone — may include chart1–chart7. Falls back to resolved `tone`, then `default`. */
  readonly iconWellTone?: IconWellTone;
  readonly icon?: ReactNode;
  readonly sparkline?: ReactNode;
  /**
   * When true, always reserve the sparkline row height so sibling KPI cards
   * in a strip share the same vertical ratio (even without a sparkline).
   */
  readonly reserveSparkline?: boolean;
  /** Plain-language caption under the value (Doplňující informace). */
  readonly hint?: string;
  /** Optional info control next to the label (e.g. MetricExplainButton). */
  readonly help?: ReactNode;
  /**
   * `truncate` (default) for numeric KPIs; `wrap` for long status phrases
   * (e.g. Czech lease copy) so narrow strips do not ellipsis mid-word.
   */
  readonly valueOverflow?: 'truncate' | 'wrap';
  /**
   * Dense chrome for dual-KPI mobile strips (~88–104px @320). md+ restores default.
   * Prefer this DS prop over grid/feature CSS padding hacks.
   */
  readonly compact?: boolean;
  /** When set, the card becomes a button (e.g. filter KPI strip). */
  readonly onClick?: () => void;
  /** Accessible name when {@link onClick} is set. */
  readonly actionLabel?: string;
  /**
   * Filter-strip honesty: the active referent is selected (ring + `aria-current`)
   * and never a dead click — `onClick` is ignored while selected.
   */
  readonly selected?: boolean;
  readonly className?: string;
}

/**
 * Shared KPI card for tenant Přehled + platform admin Přehled (and other strips).
 * Spec1 §8.5 order: Icon → Title → Value → Doplňující → Trend.
 * Put in an equal-height grid (`an-kpi-grid--one`…`--six`): cards stretch together;
 * content is center-aligned; sparklines pin to the bottom with `mt-auto`.
 *
 * Trend polarity chrome: for worse-when-up metrics (low stock, OOS, refunds, incidents)
 * invert success/danger mapping at the call site — see `kpiTrendPolarity.ts` (chrome only).
 */
export function KpiCard({
  label: labelText,
  value: valueText,
  deltaLabel,
  direction,
  tone,
  iconWellTone,
  icon,
  sparkline,
  reserveSparkline = false,
  hint,
  help,
  valueOverflow = 'truncate',
  compact = false,
  onClick,
  actionLabel,
  selected = false,
  className,
}: KpiCardProps): JSX.Element {
  const cardTone: KpiCardTone = resolveKpiCardTone(tone);
  const wellTone: IconWellTone = iconWellTone ?? cardTone;
  const hintText = hint?.trim() ?? '';
  const showSparklineSlot = sparkline != null || reserveSparkline;
  const showTrendChrome = deltaLabel != null && direction !== undefined;
  const trendDirection: DeltaDirection = direction ?? 'flat';
  const secondaryMtClass = compact ? 'mt-1 md:mt-1.5' : 'mt-1.5';
  // Selected wins: active filter tile is inert (no button / no dead click).
  const isInteractive = onClick !== undefined && !selected;

  const body = (
    <>
      {/* Spec1 §8.5: Icon → Title (column); help stays with title row */}
      <div className={headerStack({ compact })}>
        {icon ? (
          <div
            className={iconWell({ tone: wellTone, compact })}
            data-testid="kpi-card-icon-well"
            aria-hidden
          >
            {icon}
          </div>
        ) : null}
        <div className="flex w-full min-w-0 items-center justify-center gap-2.5">
          <p className={label()}>{labelText}</p>
          {help != null ? <div className="shrink-0">{help}</div> : null}
        </div>
      </div>

      <p className={value({ overflow: valueOverflow, compact })}>{valueText}</p>

      {hintText.length > 0 ? (
        <p
          className={`${secondaryMtClass} line-clamp-2 min-w-0 w-full text-center text-xs font-normal normal-case tracking-normal leading-snug text-[var(--color-an-text-muted)]`}
          data-testid="kpi-card-hint"
        >
          {hintText}
        </p>
      ) : null}

      {deltaLabel && showTrendChrome ? (
        <span
          className={delta({ direction: trendDirection, compact })}
          data-testid="kpi-card-trend"
        >
          <TrendDirectionIcon direction={trendDirection} />
          <span className="min-w-0 whitespace-normal break-words text-left [overflow-wrap:anywhere]">
            {deltaLabel}
          </span>
        </span>
      ) : null}

      {deltaLabel && !showTrendChrome ? (
        <p
          className={`${secondaryMtClass} line-clamp-2 min-w-0 w-full whitespace-normal break-words text-center text-xs font-normal leading-snug text-[var(--color-an-text-muted)] [overflow-wrap:anywhere]`}
          data-testid="kpi-card-secondary"
          title={deltaLabel}
        >
          {deltaLabel}
        </p>
      ) : null}

      {showSparklineSlot ? (
        <div className="mt-auto w-full pt-2 h-7" data-testid="kpi-card-sparkline-slot">
          {sparkline ?? null}
        </div>
      ) : null}
    </>
  );

  const selectedChrome = selected
    ? ' ring-2 ring-[var(--color-an-primary)] ring-offset-2 ring-offset-[var(--color-an-surface)]'
    : '';
  const interactiveChrome = isInteractive
    ? ' cursor-pointer transition-colors hover:bg-[var(--color-an-surface-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-an-focus-ring)]'
    : '';
  const cardClassName = kpiCard({
    tone: cardTone,
    compact,
    className: `rp-kpi-container${selectedChrome}${interactiveChrome}${className ? ` ${className}` : ''}`,
  });

  if (isInteractive) {
    return (
      <button
        type="button"
        className={cardClassName}
        data-compact={compact ? 'true' : undefined}
        data-testid="kpi-card"
        data-kpi-interactive="true"
        aria-pressed={false}
        title={valueOverflow === 'wrap' ? valueText : undefined}
        aria-label={actionLabel ?? labelText}
        onClick={onClick}
      >
        {body}
      </button>
    );
  }

  return (
    <article
      className={cardClassName}
      data-compact={compact ? 'true' : undefined}
      data-testid="kpi-card"
      data-kpi-selected={selected ? 'true' : undefined}
      aria-current={selected ? 'true' : undefined}
      title={valueOverflow === 'wrap' ? valueText : undefined}
    >
      {body}
    </article>
  );
}
